import { useState, useEffect } from 'react';
import { Clock, Copy, CheckCircle2 } from 'lucide-react';

export default function TimestampConverter() {
  const [timestamp, setTimestamp] = useState('');
  const [humanReadable, setHumanReadable] = useState('');
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [copied, setCopied] = useState<'unix' | 'iso' | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const timestampToHuman = (ts: string) => {
    try {
      const num = parseInt(ts);
      if (isNaN(num)) {
        setHumanReadable('');
        return;
      }
      // Handle both seconds and milliseconds
      const milliseconds = num.toString().length === 10 ? num * 1000 : num;
      const date = new Date(milliseconds);
      setHumanReadable(date.toISOString() + '\n\n' + date.toLocaleString());
    } catch {
      setHumanReadable('Invalid timestamp');
    }
  };

  const humanToTimestamp = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        setTimestamp('');
        return;
      }
      setTimestamp(Math.floor(date.getTime() / 1000).toString());
    } catch {
      setTimestamp('Invalid date');
    }
  };

  const copyToClipboard = (text: string, type: 'unix' | 'iso') => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg dark:to-dark-surface rounded-2xl p-6 border-2 border-gray-200 dark:border-dark-border shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-xl shadow-lg">
          <Clock className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-dark-text">Timestamp Converter</h2>
          <p className="text-sm text-gray-500 dark:text-dark-muted">Convert between Unix timestamp and human readable dates</p>
        </div>
      </div>

      {/* Current Timestamp */}
      <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 via-blue-50 to-teal-50 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-teal-900/20 rounded-xl border-2 border-purple-200 dark:border-purple-800 shadow-md">
        <h3 className="text-sm font-bold text-gray-900 dark:text-dark-text uppercase tracking-wide mb-3">Current Time</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="bg-white dark:bg-dark-surface rounded-lg p-3 border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Unix Timestamp</span>
              <button
                onClick={() => copyToClipboard(Math.floor(currentTime / 1000).toString(), 'unix')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
              >
                {copied === 'unix' ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            </div>
            <p className="text-lg font-mono font-bold text-gray-900 dark:text-dark-text">{Math.floor(currentTime / 1000)}</p>
          </div>
          <div className="bg-white dark:bg-dark-surface rounded-lg p-3 border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">ISO 8601</span>
              <button
                onClick={() => copyToClipboard(new Date(currentTime).toISOString(), 'iso')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
              >
                {copied === 'iso' ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            </div>
            <p className="text-sm font-mono text-gray-900 dark:text-dark-text">{new Date(currentTime).toISOString()}</p>
          </div>
        </div>
      </div>

      {/* Converters */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Unix to Human */}
        <div>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
            Unix Timestamp → Human Readable
          </label>
          <input
            type="text"
            value={timestamp}
            onChange={(e) => {
              setTimestamp(e.target.value);
              timestampToHuman(e.target.value);
            }}
            placeholder="Enter Unix timestamp (e.g., 1704067200)"
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent transition-all mb-2"
          />
          <textarea
            value={humanReadable}
            readOnly
            placeholder="Human readable date will appear here..."
            className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-dark-text resize-none"
          />
        </div>

        {/* Human to Unix */}
        <div>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
            Human Readable → Unix Timestamp
          </label>
          <input
            type="text"
            onChange={(e) => humanToTimestamp(e.target.value)}
            placeholder="Enter date (e.g., 2024-01-01T00:00:00Z)"
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent transition-all mb-2"
          />
          <div className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-dark-bg flex items-center justify-center">
            {timestamp && (
              <p className="text-2xl font-mono font-bold text-gray-900 dark:text-dark-text">{timestamp}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
